// src/components/subcontractors/tabs/SubOverviewTab.jsx
import { useSubcontractors } from '../../../context/SubcontractorContext';
import { SPECIALTY_TAGS } from '../subcontractorsData';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getInitials(name) {
  return (name || '')
    .split(' ')
    .filter(Boolean)
    .map(w => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2) || '?';
}

const STATUS_STYLES = {
  active:      { bg: '#10B98122', border: '#10B981', text: '#10B981' },
  inactive:    { bg: '#F59E0B22', border: '#F59E0B', text: '#F59E0B' },
  blacklisted: { bg: '#EF444422', border: '#EF4444', text: '#EF4444' },
};

const RATE_LABELS = {
  hourly:      'Per Hour',
  flat:        'Flat Rate',
  per_panel:   'Per Panel',
  per_vehicle: 'Per Vehicle',
};

function buildStars(rating) {
  // rating is already rounded to nearest 0.5
  const stars = [];
  for (let i = 1; i <= 5; i++) {
    if (rating >= i) {
      stars.push('full');
    } else if (rating >= i - 0.5) {
      stars.push('half');
    } else {
      stars.push('empty');
    }
  }
  return stars;
}

function parseLocalDate(str) {
  const [y, m, d] = str.split('T')[0].split('-').map(Number);
  return new Date(y, m - 1, d);
}

function formatDueDate(dateStr) {
  const d = parseLocalDate(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function getDueDateColor(dateStr) {
  const now = new Date();
  const due = parseLocalDate(dateStr);
  const diff = due - now;
  const oneDayMs = 86_400_000;

  if (diff < 0) return '#EF4444';           // past due — red
  if (diff <= oneDayMs * 2) return '#F59E0B'; // today or tomorrow — amber
  return '#7D93AE';                          // future — muted
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function StarRating({ rating }) {
  const stars = buildStars(rating);
  return (
    <div className="flex items-center gap-1">
      <span className="flex">
        {stars.map((type, i) => {
          if (type === 'full') {
            return (
              <span key={i} style={{ color: '#F59E0B', fontSize: 'var(--wm-text-cta)', lineHeight: 1 }}>
                ★
              </span>
            );
          }
          if (type === 'half') {
            return (
              <span key={i} style={{ position: 'relative', display: 'inline-block', fontSize: 'var(--wm-text-cta)', lineHeight: 1 }}>
                <span style={{ color: '#94A3B8' }}>★</span>
                <span
                  style={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    width: '50%',
                    overflow: 'hidden',
                    color: '#F59E0B',
                  }}
                >
                  ★
                </span>
              </span>
            );
          }
          return (
            <span key={i} style={{ color: '#94A3B8', fontSize: 'var(--wm-text-cta)', lineHeight: 1 }}>
              ★
            </span>
          );
        })}
      </span>
      <span className="text-[var(--wm-text-ui)]" style={{ color: '#7D93AE' }}>{rating.toFixed(1)}</span>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function SubOverviewTab({ sub, onEdit }) {
  const { reminders, getSubRating, toggleReminder, deleteReminder } = useSubcontractors();

  const rating = getSubRating(sub.id);
  const statusStyle = STATUS_STYLES[sub.status] ?? STATUS_STYLES.inactive;

  // Contact fields
  const contactFields = [
    sub.phone    && { label: 'Phone',    value: sub.phone, type: 'text' },
    sub.email    && { label: 'Email',    value: sub.email, type: 'email' },
    sub.address  && { label: 'Address',  value: sub.address, type: 'text' },
    sub.website  && { label: 'Website',  value: sub.website, type: 'url' },
    sub.instagram && { label: 'Instagram', value: sub.instagram, type: 'instagram' },
  ].filter(Boolean);

  // Specialty tags
  const specialtyTags = (sub.specialties || [])
    .map(id => SPECIALTY_TAGS.find(t => t.id === id))
    .filter(Boolean);

  // Default rate display
  const rateLabel = RATE_LABELS[sub.defaultRate?.type] ?? sub.defaultRate?.type;
  const rateAmount = sub.defaultRate?.amount ?? 0;
  const rateDisplay = rateAmount > 0
    ? `$${Number(rateAmount).toLocaleString()} ${rateLabel}`
    : 'Not set';

  // Active reminders — undone, for this sub, sorted ascending by dueDate, max 5
  const pendingReminders = reminders
    .filter(r => r.subcontractorId === sub.id && !r.done)
    .sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate))
    .slice(0, 5);

  return (
    <div className="space-y-6">

      {/* ── Section 1: Profile card ─────────────────────────────────────────── */}
      <div className="rounded-xl p-5 border bg-white dark:bg-[#0F1923] border-gray-200 dark:border-[#1E3048]">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-4">
            {/* Avatar */}
            <div
              className="w-16 h-16 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ background: sub.avatarColor || '#2563eb' }}
            >
              <span className="text-[var(--wm-text-heading)] font-bold text-white">
                {getInitials(sub.name)}
              </span>
            </div>

            {/* Name / company / status / rating */}
            <div className="flex flex-col gap-1">
              <span className="text-[var(--wm-text-heading)] font-semibold text-[#0F1923] dark:text-[#F8FAFE]">
                {sub.name}
              </span>

              {sub.company && (
                <span className="text-[var(--wm-text-body)] text-[#64748B] dark:text-[#7D93AE]">
                  {sub.company}
                </span>
              )}

              <div className="flex items-center gap-2 flex-wrap">
                {/* Status badge */}
                <span
                  className="px-2 py-0.5 rounded-full text-[var(--wm-text-ui)] font-medium border capitalize"
                  style={{
                    background: statusStyle.bg,
                    borderColor: statusStyle.border,
                    color: statusStyle.text,
                  }}
                >
                  {sub.status}
                </span>

                {/* Star rating */}
                {rating != null && <StarRating rating={rating} />}
              </div>
            </div>
          </div>

          {/* Edit button */}
          <button
            type="button"
            onClick={onEdit}
            className="flex-shrink-0 px-3 py-1 text-[var(--wm-text-ui)] border rounded-lg transition-colors border-gray-200 dark:border-[#243348] text-[#64748B] dark:text-[#7D93AE] hover:text-[#0F1923] dark:hover:text-[#F8FAFE] hover:border-[#2E8BF0]"
          >
            Edit Profile
          </button>
        </div>
      </div>

      {/* ── Section 2: Contact info ─────────────────────────────────────────── */}
      <div className="rounded-xl p-5 border bg-white dark:bg-[#0F1923] border-gray-200 dark:border-[#1E3048]">
        <p className="text-[var(--wm-text-ui)] font-semibold uppercase tracking-wide mb-3 text-[#64748B] dark:text-[#7D93AE]">
          Contact Info
        </p>

        {contactFields.length === 0 ? (
          <p className="text-[var(--wm-text-body)] text-[#64748B] dark:text-[#7D93AE]">No contact info added.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
            {contactFields.map(({ label, value, type }) => (
              <div key={label} className="flex flex-col gap-0.5">
                <span className="text-[var(--wm-text-ui)] text-[#64748B] dark:text-[#7D93AE]">{label}</span>
                {type === 'url' && (
                  <a
                    href={value}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[var(--wm-text-body)] truncate"
                    style={{ color: '#2E8BF0' }}
                  >
                    {value}
                  </a>
                )}
                {type === 'instagram' && (
                  <a
                    href={`https://instagram.com/${value.replace(/^@/, '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[var(--wm-text-body)]"
                    style={{ color: '#2E8BF0' }}
                  >
                    {value.startsWith('@') ? value : `@${value}`}
                  </a>
                )}
                {type === 'email' && (
                  <a
                    href={`mailto:${value}`}
                    className="text-[var(--wm-text-body)]"
                    style={{ color: '#2E8BF0' }}
                  >
                    {value}
                  </a>
                )}
                {type === 'text' && (
                  <span className="text-[var(--wm-text-body)] text-[#0F1923] dark:text-[#F8FAFE]">{value}</span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Section 3: Specialties + Rate + Notes ──────────────────────────── */}
      <div className="rounded-xl p-5 border bg-white dark:bg-[#0F1923] border-gray-200 dark:border-[#1E3048]">
        {/* Specialties */}
        <p className="text-[var(--wm-text-ui)] font-semibold uppercase tracking-wide mb-3 text-[#64748B] dark:text-[#7D93AE]">
          Specialties
        </p>

        {specialtyTags.length === 0 ? (
          <p className="text-[var(--wm-text-body)] mb-4 text-[#64748B] dark:text-[#7D93AE]">None listed.</p>
        ) : (
          <div className="flex flex-wrap gap-2 mb-4">
            {specialtyTags.map(tag => (
              <span
                key={tag.id}
                className="px-3 py-1 rounded-full text-[var(--wm-text-ui)] font-medium border"
                style={{
                  background: tag.color + '33',
                  borderColor: tag.color,
                  color: tag.color,
                }}
              >
                {tag.label}
              </span>
            ))}
          </div>
        )}

        {/* Default Rate */}
        <p className="text-[var(--wm-text-ui)] font-semibold uppercase tracking-wide mb-1 text-[#64748B] dark:text-[#7D93AE]">
          Default Rate
        </p>
        <p className="text-[var(--wm-text-body)] mb-4 text-[#0F1923] dark:text-[#F8FAFE]">
          {rateDisplay}
        </p>

        {/* Notes */}
        {sub.notes && sub.notes.trim() && (
          <>
            <p className="text-[var(--wm-text-ui)] font-semibold uppercase tracking-wide mb-1 text-[#64748B] dark:text-[#7D93AE]">
              Notes
            </p>
            <p className="text-[var(--wm-text-body)] whitespace-pre-wrap text-[#64748B] dark:text-[#7D93AE]">
              {sub.notes}
            </p>
          </>
        )}
      </div>

      {/* ── Section 4: Reminders ───────────────────────────────────────────── */}
      <div className="rounded-xl p-5 border bg-white dark:bg-[#0F1923] border-gray-200 dark:border-[#1E3048]">
        <div className="flex items-center gap-2 mb-3">
          <p className="text-[var(--wm-text-ui)] font-semibold uppercase tracking-wide text-[#64748B] dark:text-[#7D93AE]">
            Reminders
          </p>
          {pendingReminders.length > 0 && (
            <span
              className="px-1.5 py-0.5 rounded-full text-[var(--wm-text-ui)] font-semibold"
              style={{ background: '#2E8BF022', color: '#2E8BF0', border: '1px solid #2E8BF044' }}
            >
              {pendingReminders.length}
            </span>
          )}
        </div>

        {pendingReminders.length === 0 ? (
          <p className="text-[var(--wm-text-body)] text-[#64748B] dark:text-[#7D93AE]">No pending reminders.</p>
        ) : (
          <ul className="space-y-2">
            {pendingReminders.map(reminder => {
              const dueDateColor = getDueDateColor(reminder.dueDate);
              return (
                <li
                  key={reminder.id}
                  className="flex items-center gap-3 py-1.5 px-2 rounded-lg bg-gray-100 dark:bg-[#111C27]"
                >
                  {/* Checkbox */}
                  <button
                    type="button"
                    onClick={() => toggleReminder(reminder.id)}
                    className="flex-shrink-0 w-4 h-4 rounded border border-gray-300 dark:border-[#243348] flex items-center justify-center transition-colors bg-transparent"
                    aria-label={`Mark "${reminder.title}" as done`}
                  >
                    {/* unchecked state — empty box */}
                  </button>

                  {/* Title */}
                  <span className="flex-1 text-[var(--wm-text-body)] truncate text-[#0F1923] dark:text-[#F8FAFE]">
                    {reminder.title}
                  </span>

                  {/* Due date */}
                  <span className="text-[var(--wm-text-ui)] flex-shrink-0" style={{ color: dueDateColor }}>
                    {formatDueDate(reminder.dueDate)}
                  </span>

                  {/* Delete */}
                  <button
                    type="button"
                    onClick={() => deleteReminder(reminder.id)}
                    className="flex-shrink-0 text-[var(--wm-text-body)] leading-none transition-colors text-[#64748B] dark:text-[#7D93AE] hover:text-[#EF4444]"
                    aria-label={`Delete reminder: ${reminder.title}`}
                  >
                    ×
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

    </div>
  );
}
